import { ButtonHTMLAttributes, useContext, useState } from 'react'
import cx from 'classnames'

import { SanityAddToCartButton } from '@data/sanity/queries/types/content'
import { CartContext } from '@lib/cart'
import { useGetFormattedPrice } from '@lib/helpers'
import { productVariantIdAttributeKey } from '@lib/shopify/cart'
import { StringsContext } from '@lib/strings-context'

import Button, { ButtonProps } from './button'

type AddToCartButtonProps = ButtonProps &
  Pick<SanityAddToCartButton, 'productVariant'> &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    onAddToCartError?: () => void
  }

const AddToCartButton = ({
  variant,
  size,
  color,
  icon,
  iconAlignment,
  children,
  className,
  productVariant,
  onAddToCartError,
}: AddToCartButtonProps) => {
  const { cart, addItemsToCart } = useContext(CartContext)
  const strings = useContext(StringsContext)
  const getFormattedPrice = useGetFormattedPrice()
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [isAddToCartError, setIsAddToCartError] = useState(false)

  const handleAddToCart = async () => {
    setIsAddToCartError(false)
    setIsAddingToCart(true)

    const feeLineItems =
      productVariant.fees?.map((fee) => ({
        id: fee.variantID,
        quantity: 1,
        attributes: [
          {
            key: `_${productVariantIdAttributeKey}`,
            value: productVariant.variantID.toString(),
          },
        ],
      })) ?? []

    const isSuccessful = await addItemsToCart(async () => [
      {
        id: productVariant.variantID,
        quantity: 1,
      },
      ...feeLineItems,
    ])

    if (!isSuccessful && onAddToCartError) {
      onAddToCartError()
    }

    setIsAddingToCart(false)
  }

  return (
    <span className={cx('inline-flex items-center', className)}>
      <span
        className={cx('font-serif font-medium mr-5', {
          'text-lg': size === 'small',
          'text-3xl': size === 'normal',
          'text-4xl': size === 'large',
        })}
      >
        {getFormattedPrice(productVariant.price)}
      </span>

      <Button
        id="product-add-to-cart-button"
        variant={variant}
        size={size}
        color={color}
        icon={icon}
        iconAlignment={iconAlignment}
        onClick={handleAddToCart}
        disabled={
          !cart || !productVariant.inStock || isAddingToCart || isAddToCartError
        }
      >
        {!cart && strings.buttonUnavailable}
        {isAddingToCart && strings.buttonAdding}
        {!productVariant.inStock && strings.productOutOfStock}
        {cart &&
          productVariant.inStock &&
          !isAddingToCart &&
          !isAddToCartError &&
          children}
      </Button>
    </span>
  )
}

export default AddToCartButton
